/**
 * Auth context for the Saee backoffice. Session is persisted via HttpOnly cookie.
 * On mount calls GET /api/auth/me. Unauthorized handler redirects to /login.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getSessionToken, setSessionToken as storeSetSessionToken } from '@services/sessionStore';
import { getStaffMe, type AuthLoginContext, type AuthUserRole } from '@services/api';
import {
  registerBrokerApiUnauthorizedHandler,
  setHadAuthenticatedSession,
} from '@services/authSessionEvents';
import { LOGIN_PATH } from '@lib/routes';
import { clearPlatformStorage } from '@utils/resetStorage';

export interface AuthContextValue {
  isAuthenticated: boolean;
  authLoading: boolean;
  setAuthenticated: (value: boolean) => void;
  sessionToken: string | null;
  setSessionToken: (token: string | null, authenticated?: boolean) => void;
  clearSession: () => void;
  refreshAuth: () => Promise<boolean>;
  loginContext: AuthLoginContext | null;
  userRole: AuthUserRole | null;
  userId: string | null;
  userPermissions: string[];
  mfaEnabled: boolean;
  userEmail: string | null;
  userName: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setAuthenticatedState] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [sessionToken, setSessionTokenState] = useState<string | null>(() => getSessionToken());
  const [loginContext, setLoginContext] = useState<AuthLoginContext | null>(null);
  const [userRole, setUserRole] = useState<AuthUserRole | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  const applyMeSuccess = useCallback((data: { id: string; name: string; email: string; role: string; mfaEnabled: boolean; permissions: string[] }) => {
    storeSetSessionToken(null);
    setSessionTokenState(null);
    setAuthenticatedState(true);
    setLoginContext('broker' as AuthLoginContext);
    const r = String(data.role ?? 'system_administrator').trim().toLowerCase();
    setUserRole(r);
    setUserId(data.id ?? null);
    setUserEmail(data.email ?? null);
    setUserName(data.name ?? null);
    setUserPermissions(data.permissions ?? []);
    setMfaEnabled(data.mfaEnabled ?? false);
    setHadAuthenticatedSession(true);
  }, []);

  const applyMeFailure = useCallback(() => {
    storeSetSessionToken(null);
    setSessionTokenState(null);
    setAuthenticatedState(false);
    setLoginContext(null);
    setUserRole(null);
    setUserId(null);
    setUserPermissions([]);
    setUserName(null);
    setHadAuthenticatedSession(false);
  }, []);

  const refreshAuth = useCallback(async (): Promise<boolean> => {
    try {
      const { data } = await getStaffMe();
      applyMeSuccess(data);
      return true;
    } catch {
      applyMeFailure();
      return false;
    }
  }, [applyMeFailure, applyMeSuccess]);

  /** Re-sync permissions after tab focus so sidebar and PermissionGuard react to RBAC changes. */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void refreshAuth();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refreshAuth]);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional
    setAuthLoading(true);
    getStaffMe()
      .then(({ data }) => {
        if (!cancelled) applyMeSuccess(data);
      })
      .catch(() => {
        if (!cancelled) applyMeFailure();
      })
      .finally(() => {
        if (!cancelled) setAuthLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applyMeFailure, applyMeSuccess]);

  const setAuthenticated = useCallback((value: boolean) => {
    setAuthenticatedState(value);
  }, []);

  const setSessionToken = useCallback(
    (token: string | null, authenticated = true) => {
      storeSetSessionToken(token);
      setSessionTokenState(token);
      if (!token) {
        setAuthenticatedState(false);
        setLoginContext(null);
        setUserRole(null);
        setUserId(null);
        setUserEmail(null);
        setHadAuthenticatedSession(false);
        return;
      }
      setAuthenticatedState(authenticated && !!token);
      if (authenticated && token) {
        setHadAuthenticatedSession(true);
      }
    },
    [],
  );

  const clearSession = useCallback(() => {
    storeSetSessionToken(null);
    setSessionTokenState(null);
    setAuthenticatedState(false);
    setLoginContext(null);
    setUserRole(null);
    setUserId(null);
    setUserPermissions([]);
    setMfaEnabled(false);
    setUserEmail(null);
    setUserName(null);
    setHadAuthenticatedSession(false);
  }, []);

  useEffect(() => {
    registerBrokerApiUnauthorizedHandler(() => {
      clearPlatformStorage();
      clearSession();
      if (typeof window !== 'undefined') {
        window.location.assign(LOGIN_PATH);
      }
    });
    return () => registerBrokerApiUnauthorizedHandler(null);
  }, [clearSession]);

  const value: AuthContextValue = {
    isAuthenticated,
    authLoading,
    setAuthenticated,
    sessionToken,
    setSessionToken,
    clearSession,
    refreshAuth,
    loginContext,
    userRole,
    userId,
    userPermissions,
    mfaEnabled,
    userEmail,
    userName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
